import React from 'react'

/**
 * LEFT/RIGHT arrow-key movement between focusable items; links stay real links, not an ARIA tablist.
 */
const useArrowKeyNav = <T extends HTMLElement = HTMLElement>(
    itemSelector: string
) => {
    const containerRef = React.useRef<T>(null)

    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return
            }

            const container = containerRef.current
            if (!container) {
                return
            }

            const items = Array.from(
                container.querySelectorAll<HTMLElement>(itemSelector)
            ).filter((item) => !item.hasAttribute('disabled'))
            if (items.length === 0) {
                return
            }

            const currentIndex = items.indexOf(
                document.activeElement as HTMLElement
            )
            if (currentIndex === -1) {
                return
            }

            event.preventDefault()
            const delta = event.key === 'ArrowRight' ? 1 : -1
            const nextIndex =
                (currentIndex + delta + items.length) % items.length
            items[nextIndex].focus()
        },
        [itemSelector]
    )

    return { containerRef, onKeyDown }
}

export default useArrowKeyNav
